import { useRef, useState } from 'react'
import type { DragEvent, PointerEvent } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { InlineEdit } from '@/components/ui/InlineEdit'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { EllipsisIcon, MoveHorizontalIcon } from 'lucide-react'

import _clamp from 'lodash/clamp'
import { cn } from 'tailwind-variants'

import { CommandMenuItems } from '@/shared/commands/CommandMenuItems'
import { dragStartInterceptor } from '@/utils/dragStartInterceptor.js'

import { COLUMN_MAX_WIDTH, COLUMN_MIN_WIDTH } from '~shared/rundown/column'
import type { Column } from '~shared/rundown/column'

import { useColumnSnapshot } from '../data/columns.store.ts'
import { gridSelectionActions, gridStateActions } from '../grid/grid.store.ts'

import type { ColumnAccess } from './column-access.rules.ts'
import { useColumnAccess } from './column-access.ts'
import { columnLayoutActions, useColumnWidth } from './column-layout.ts'
import { columnActions } from './column.actions.ts'
import { columnMenuGroups, useColumnCommandContext } from './column.commands.ts'
import { getColumnNameDomId } from './column.const.ts'

type Props = {
  columnId: Column['id']
  onDragStart: (columnId: string) => void
  onDragEnd: () => void
  onDragOver: (event: DragEvent<HTMLDivElement>, columnId: string) => void
  isDragging: boolean
}

/**
 * Header for one column.
 *
 * @remarks
 *   Renders nothing while the column document is missing. On delete, the store
 *   drops it a microtask before the derived list does.
 */
export function ColumnHeader({ columnId, ...props }: Props) {
  const column = useColumnSnapshot(columnId)
  if (!column) return null

  return (
    <ColumnHeaderContent
      column={column}
      {...props}
    />
  )
}

function ColumnHeaderContent({
  column,
  onDragStart,
  onDragEnd,
  onDragOver,
  isDragging,
}: Omit<Props, 'columnId'> & { column: Column }) {
  const access = useColumnAccess(column)
  const nameTooltip = getNameTooltip(access)

  const containerRef = useRef<HTMLDivElement>(null)
  const savedWidth = useColumnWidth(column)
  // Only set while resizing; the header previews it, the grid updates on release
  const [dragWidth, setDragWidth] = useState<number | null>(null)
  const isResizing = dragWidth !== null

  function onResizeStart(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragWidth(savedWidth)
  }

  function onResizeMove(event: PointerEvent<HTMLDivElement>) {
    if (!isResizing || !containerRef.current) return
    const { left } = containerRef.current.getBoundingClientRect()
    // Round: the header's left edge can sit on a half pixel
    setDragWidth(
      Math.round(
        _clamp(event.clientX - left, COLUMN_MIN_WIDTH, COLUMN_MAX_WIDTH),
      ),
    )
  }

  function onResizeEnd() {
    if (!isResizing) return
    setDragWidth(null)
    if (dragWidth === savedWidth) return
    // Always write local
    columnLayoutActions.setWidth(column.id, dragWidth)
    // Also push to DB, if allowed. Failures already toast.
    if (access.canWriteColumn)
      columnActions.update(column.id, { width: dragWidth }).catch(() => {})
  }

  return (
    <div
      ref={containerRef}
      draggable={true}
      onDragStart={(event) =>
        dragStartInterceptor(() => onDragStart(column.id), event.nativeEvent)
      }
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, column.id)}
      onContextMenu={(e) => {
        // Keep the native menu (paste, spellcheck) while renaming
        if (e.target instanceof Element && e.target.closest('[data-editing]'))
          return

        e.preventDefault()

        gridSelectionActions.clear() // Prevent accidental hotkeys for cell/cue

        gridStateActions.contextMenu.open({
          type: 'column',
          columnId: column.id,
          x: e.clientX,
          y: e.clientY,
        })
      }}
      style={{
        width: `${dragWidth ?? savedWidth}px`,
      }}
      className="group relative"
    >
      {/* Header container */}
      <div className="relative flex items-center justify-between border-b-2 border-white/20">
        {/* Left side - Column name  */}
        <Tooltip disabled={!nameTooltip}>
          <TooltipTrigger
            aria-label={nameTooltip ?? 'Column name'}
            render={<div />}
            className="flex items-baseline gap-1 overflow-hidden min-w-0"
          >
            {/* Labels the columnheader; wraps InlineEdit, which swaps elements */}
            <span
              id={getColumnNameDomId(column.id)}
              className="contents"
            >
              <InlineEdit
                className={cn(
                  'flex-1 h-6 leading-6 text-sm min-w-0 truncate',
                  access.isOwnPrivate ? 'text-yellow-300' : 'text-white',
                )}
                value={column.name}
                placeholder="Untitled"
                onValueCommit={(name) =>
                  columnActions.update(column.id, { name })
                }
                disabled={!access.canConfigure}
              />
            </span>

            {access.isGuestEditable && (
              <span className="flex-none text-neutral-400 text-xs">
                (Editable)
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent>{nameTooltip}</TooltipContent>
        </Tooltip>

        {/* Right side - Buttons. Touch has no hover, so it always shows the
            menu (with its backdrop) and hides the drag handle. */}
        <div className="flex items-center gap-1 flex-none group-hover:bg-black/50 group-hover:backdrop-blur-md has-focus-visible:bg-black/50 has-focus-visible:backdrop-blur-md pointer-coarse:bg-black/50 pointer-coarse:backdrop-blur-md rounded-t-md absolute top-0 right-0 pl-1 pr-2 group-has-data-editing:hidden">
          <ColumnHeaderMenu columnId={column.id} />

          <Tooltip>
            <TooltipTrigger
              aria-label="Drag to move column"
              render={<div />}
              data-draghandle
              className="flex h-6 w-6 items-center justify-center opacity-0 group-hover:opacity-50 hover:opacity-100 pointer-coarse:hidden cursor-grab active:cursor-grabbing transition-opacity"
            >
              <MoveHorizontalIcon className="size-4" />
            </TooltipTrigger>
            <TooltipContent>Drag to move column</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize column"
        data-resize-handle
        data-resizing={isResizing || undefined}
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onLostPointerCapture={() => setDragWidth(null)}
        className="absolute top-0 -right-2 h-6 w-4 touch-none cursor-col-resize"
      />

      {/* Full-height guide line, while hovering or dragging the resize handle */}
      {!isDragging && (
        <span className="absolute -right-1 hidden w-0.5 h-screen bg-gray-500 pointer-events-none group-has-[[data-resize-handle]:hover]:block group-has-data-resizing:block" />
      )}
    </div>
  )
}

/**
 * Tooltip on the column name, or none when it has nothing to say.
 */
function getNameTooltip(access: ColumnAccess): string | undefined {
  if (access.isOwnPrivate) return 'Only you can see this column.'
  if (access.canConfigure) return 'Edit column name'
  if (access.isGuestEditable) return 'You can edit content in this column.'
  return undefined
}

/**
 * The "..." menu on a column header.
 */
function ColumnHeaderMenu({ columnId }: { columnId: Column['id'] }) {
  const context = useColumnCommandContext(columnId)
  if (!context) return null

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger
          aria-label="Column options"
          render={
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="opacity-0 group-hover:opacity-50 pointer-coarse:opacity-50 hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100 transition-opacity"
                >
                  <EllipsisIcon />
                </Button>
              }
            />
          }
        />
        <TooltipContent>Column options</TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        align="end"
        className="w-auto"
      >
        <CommandMenuItems
          groups={columnMenuGroups}
          context={context}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
